import { inject } from '@angular/core';
import { type CanDeactivateFn, type CanMatchFn } from '@angular/router';
import { PermissionsService } from '@frontendlib/common/features/permissions/services/permissions.service';
import { type Actions, type Subjects, ViewSubjects } from '@sharedlib/common/permissions/types/Permissions';
import { filter, map, take } from 'rxjs';
import { BoardsEditorStore } from '../services/boards-editor.store';

/**
 * Whether `/boards` is the Boards shell or the unavailable state. Same permission and same wait as `BoardsAppGuard`
 * (`boardsView`, once permissions have loaded), but a refusal answers `false` so the router falls through to the next
 * `boards` route (the sentinel's unavailable page) instead of redirecting back to itself.
 */
export const boardsAppEnabledMatch: CanMatchFn = () => {
  const permissions: PermissionsService<Actions, Subjects> = inject(PermissionsService);
  return permissions.hasPermissionsLoadedObservable.pipe(
    filter(Boolean),
    take(1),
    map(() => permissions.checkPermission('read', ViewSubjects.boardsView)),
  );
};

/**
 * Leaving Boards (another app, a link) saves the open board first; a failed save asks, and "Cancel" keeps the editor and
 * its full-window session active. Only an accepted departure lets the layout's destroy end the session.
 */
export const boardsCanDeactivate: CanDeactivateFn<unknown> = () => inject(BoardsEditorStore).confirmDeparture();
